import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import './ReferralsDetails.scss'
import { BnpmPromoWinXModel } from 'BoostXpressAdmin/Model/bnpmPromoXModel'
import { ReferalXModel, ReferralListXModel } from 'BoostXpressAdmin/Model/referalXModel'
import { bnpmPromoBXService } from 'BoostXpressAdmin/Services/bnpmPromoBXService'
import { referralBXService } from 'BoostXpressAdmin/Services/referralBXService'
import { formatString } from 'Helpers/formatString'
import backArrow from '../../../../../Assets/Icon/Users/back-arrow-detailed.svg'
import breadcrumbArrow from '../../../../../Assets/Icon/Referrals/breadcrumb-arrow.svg'
import { HorizontalLoader } from 'Components'

const deriveCycleCount = (referral: ReferalXModel) => {
  const rawValue = Math.round(
    referral.totalRewardPercentage || referral.referrerSharePercentage || 0,
  )
  return rawValue > 0 ? rawValue : 1
}

const deriveTransactionCount = (referral: ReferalXModel) =>
  referral.status === 'ORDER_PLACED' ? 1 : 0

const formatCurrency = (amount: number) => `₦${amount.toLocaleString()}`

const formatDateTime = (value?: string | null) => {
  if (!value) return '-'
  return new Date(value).toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: false,
  })
}

const ReferralsDetails = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [isLoading, setIsLoading] = useState(false)
  const [isReferralsLoading, setIsReferralsLoading] = useState(false)
  const [referral, setReferral] = useState<ReferalXModel | null>(null)
  const [win, setWin] = useState<BnpmPromoWinXModel | null>(null)
  const [referralsList, setReferralsList] = useState<ReferralListXModel[]>([])

  useEffect(() => {
    let cancelled = false

    const loadDetails = async () => {
      if (!id) return

      try {
        setIsLoading(true)
        const res = await bnpmPromoBXService.showWin(id)
        if (cancelled) return
        setWin(res.data.data ?? null)
        setReferral(null)
      } catch {
        try {
          const res = await referralBXService.show(id)
          if (cancelled) return
          setReferral(res.data.data ?? null)
          setWin(null)
        } catch {
          if (!cancelled) {
            setReferral(null)
            setWin(null)
          }
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    loadDetails()

    return () => {
      cancelled = true
    }
  }, [id])

  useEffect(() => {
    let cancelled = false

    const loadReferralsList = async () => {
      const referrerId = referral?.referrer.resourceId || win?.user?.resourceId
      if (!referrerId) return

      try {
        setIsReferralsLoading(true)
        const res = await referralBXService.getByReferrerId(referrerId, { page: 1, limit: 50, sortOrder: 'DESC' })
        if (cancelled) return
        setReferralsList(res.data.data ?? [])
      } catch {
        if (!cancelled) {
          setReferralsList([])
        }
      } finally {
        if (!cancelled) setIsReferralsLoading(false)
      }
    }

    loadReferralsList()

    return () => {
      cancelled = true
    }
  }, [referral, win])

  const cycleCount = referral ? deriveCycleCount(referral) : 0
  const transactionCount = referral ? deriveTransactionCount(referral) : 0
  const totalAmountWon = win
    ? (win.amountMajor ?? 0)
    : referral
      ? referral.referrerRewardMinor / 100
      : 0

  const historyRows = useMemo(() => {
    // if (win) {
    //   return [
    //     {
    //       id: win.resourceId,
    //       transactionAmount: win.order?.costMajor ?? 0,
    //       amountWon: win.amountMajor ?? 0,
    //       dateTime: win.createdAt,
    //     },
    //   ]
    // }

    if (!referral) return []

    const rows = [
      {
        id: 'created',
        transactionAmount:
          referral.referredRewardMinor > 0
            ? referral.referredRewardMinor / 100
            : referral.referrerRewardMinor / 100,
        amountWon: referral.referrerRewardMinor / 100,
        dateTime: referral.createdAt,
      },
    ]

    // if (referral.completedAt) {
    //   rows.push({
    //     id: 'completed',
    //     transactionAmount: referral.referrerRewardMinor / 100,
    //     amountWon: referral.referrerRewardMinor / 100,
    //     dateTime: referral.completedAt,
    //   })
    // }

    return rows
  }, [referral])

  return (
    <div className='bx-bnpm-details'>
      <div className='bx-bnpm-details__breadcrumb'>
        <button type='button' className='bx-bnpm-details__back' onClick={() => navigate(-1)}>
          <img src={backArrow} alt='Back' />
        </button>
        <div className='bx-bnpm-details__crumbs'>
          <Link to='/boostxpress/referrals' className='bx-bnpm-details__crumb-link'>
            Referral Management
          </Link>
          <img src={breadcrumbArrow} alt='' />
          <span className='bx-bnpm-details__crumb-current'>Referral Details</span>
        </div>
      </div>

      <div className='bx-bnpm-details__heading'>
        <h2>
          {isLoading
            ? 'Loading...'
            : win
              ? `${win.user?.firstName ?? ''} ${win.user?.lastName ?? ''}`.trim() || 'BNPM Win'
              : referral
                ? `${referral.referrer.firstName} ${referral.referrer.lastName}`
                : 'Details not found'}
        </h2>
      </div>

      <div className='row g-4'>
        <div className='col-xl-6'>
          <section className='bx-bnpm-details__section'>
            <h3 className='bx-bnpm-details__section-title'>Referral Details</h3>
            <div className='bx-bnpm-details__info-card'>
              <div className='bx-bnpm-details__info-row'>
                <span>Customer Name</span>
                <strong>
                  {referral?.referred?.firstName ?? ''} {referral?.referred?.lastName ?? ''}
                </strong>
              </div>
              <div className='bx-bnpm-details__info-row'>
                <span>Customer Email</span>
                <strong>{win?.user?.email ?? referral?.referred.email ?? '-'}</strong>
              </div>
              <div className='bx-bnpm-details__info-row'>
                <span>Date</span>
                <strong>{formatString(referral?.createdAt)}</strong>
              </div>
              {/* <div className='bx-bnpm-details__info-row'>
                <span>{win ? 'Win Type' : 'No of Cycles'}</span>
                <strong>{win ? formatString(win.winType) : referral ? cycleCount : '-'}</strong>
              </div> */}
              {/* <div className='bx-bnpm-details__info-row'>
                <span>{win ? 'Order Reference' : 'Transaction Count'}</span>
                <strong>
                  {win
                    ? (win.order?.referenceNumber ?? '-')
                    : referral
                      ? `${transactionCount} of ${cycleCount}`
                      : '-'}
                </strong>
              </div> */}
            </div>
          </section>
        </div>

        <div className='col-xl-6'>
          <section className='bx-bnpm-details__section'>
            <h3 className='bx-bnpm-details__section-title'>Referral Amount</h3>
            <div className='bx-bnpm-details__amount-card'>
              <span>{formatCurrency(totalAmountWon)}</span>
            </div>
          </section>
        </div>
      </div>

      <section className='bx-bnpm-details__section bx-bnpm-details__section--table'>
        <h3 className='bx-bnpm-details__section-title'>Transaction History</h3>

        <div className='bx-bnpm-details__table-shell'>
          <div className='table-responsive'>
            <table className='bx-bnpm-details__table'>
              <thead>
                <tr>
                  <th>S/N</th>
                  <th>Customer Name</th>
                  <th>Transaction Amount</th>
                  <th>Referral Amount</th>
                  <th>Date & Time</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={4} className='bx-bnpm-details__table-cell text-center'>
                       <HorizontalLoader></HorizontalLoader>
                    </td>
                  </tr>
                ) : historyRows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className='bx-bnpm-details__table-cell text-center'>
                      No transaction history available
                    </td>
                  </tr>
                ) : (
                  historyRows.map((row, index) => (
                    <tr key={row.id}>
                      <td className='bx-bnpm-details__table-cell'>{index + 1}</td>
                      <td className='bx-bnpm-details__table-cell'>
                        {referral?.referred?.firstName ?? ''} {referral?.referred?.lastName ?? ''}
                      </td>
                      <td className='bx-bnpm-details__table-cell'>
                        {formatCurrency(row.transactionAmount)}
                      </td>
                      <td className='bx-bnpm-details__table-cell'>
                        {/* {formatCurrency(row.amountWon)} */}
                        {formatCurrency(totalAmountWon)}
                      </td>
                      <td className='bx-bnpm-details__table-cell'>
                        {formatDateTime(row.dateTime)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className='bx-bnpm-details__section bx-bnpm-details__section--table'>
        <h3 className='bx-bnpm-details__section-title'>Referrals List</h3>

        <div className='bx-bnpm-details__table-shell'>
          <div className='table-responsive'>
            <table className='bx-bnpm-details__table'>
              <thead>
                <tr>
                  <th>S/N</th>
                  <th>Referred Customer</th>
                  <th>Status</th>
                  <th>Referrer Reward</th>
                  <th>Referred Reward</th>
                  <th>Date & Time</th>
                </tr>
              </thead>
              <tbody>
                {isReferralsLoading ? (
                  <tr>
                    <td colSpan={6} className='bx-bnpm-details__table-cell text-center'>
                      <HorizontalLoader></HorizontalLoader>
                    </td>
                  </tr>
                ) : referralsList.length === 0 ? (
                  <tr>
                    <td colSpan={6} className='bx-bnpm-details__table-cell text-center'>
                      No referrals available
                    </td>
                  </tr>
                ) : (
                  referralsList.map((referralItem, index) => (
                    <tr key={referralItem.resourceId}>
                      <td className='bx-bnpm-details__table-cell'>{index + 1}</td>
                      <td className='bx-bnpm-details__table-cell'>
                        {referralItem.referred.firstName} {referralItem.referred.lastName}
                        <br />
                        <small className='text-muted'>{referralItem.referred.email}</small>
                      </td>
                      <td className='bx-bnpm-details__table-cell'>
                        <span className={`badge ${referralItem.status === 'SIGNED_UP' ? 'badge-success' : 'badge-secondary'}`}>
                          {referralItem.status}
                        </span>
                      </td>
                      <td className='bx-bnpm-details__table-cell'>
                        {formatCurrency(referralItem.referrerRewardMinor / 100)}
                      </td>
                      <td className='bx-bnpm-details__table-cell'>
                        {formatCurrency(referralItem.referredRewardMinor / 100)}
                      </td>
                      <td className='bx-bnpm-details__table-cell'>
                        {formatDateTime(referralItem.createdAt)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  )
}

export default ReferralsDetails
